import { FontAwesomeIcon, FontAwesomeLayers, FontAwesomeLayersText } from '@fortawesome/vue-fontawesome'
import AcceptPoliciesCheckbox from './forms/AcceptPoliciesCheckbox'
import ActionButton from './forms/ActionButton'
import AdminBar from './AdminBar'
import Alert from './messages/Alert'
import AlertMessage from './messages/AlertMessage'
import CarbonAd from './ads/CarbonAd'
import CodeBlock from './code/CodeBlock'
import CodeBlockTabSet from './code/CodeBlockTabSet'
import CodeHighlight from './syntax/CodeHighlight'
import CookieConsent from './messages/CookieConsent'
import ElementLoading from './loaders/ElementLoading'
import FlashMessage from './messages/FlashMessage.vue'
import Frameworks from './content/Frameworks'
import FontAwesomeAd from './ads/FontAwesomeAd'
import ForceUpdateOnMounted from './ForceUpdateOnMounted'
import IconsLoading from './loaders/IconsLoading'
import LogoFlag from './branding/LogoFlag'
import LogoFontAwesome from './branding/LogoFontAwesome'
import Media from './containers/Media'
import Message from './messages/Message'
import ModalAutoRenew from './modals/ModalAutoRenew'
import ModalCancelSubscription from './modals/ModalCancelSubscription'
import ModalFrame from './modals/ModalFrame'
import ModalKitsAndDomains from './modals/ModalKitsAndDomains'
import ModalUseKit from './modals/ModalUseKit'
import ModalUpsell from './modals/ModalUpsell'
import MonetizationAd from './ads/MonetizationAd'
import Nudge from './messages/Nudge'
import ObfuscatedText from './content/ObfuscatedText'
import VersionAlert from './messages/VersionAlert'
import Page from './containers/Page'
import PageFocused from './containers/PageFocused'
import PageFooter from './PageFooter'
import PageFooterSimple from './PageFooter/Simple.vue'
import PageHeader from './PageHeader'
import PageHeaderLimited from './PageHeader/Limited.vue'
import PageHeaderSimple from './PageHeader/Simple.vue'
import PageLoading from './loaders/PageLoading'
import PagePurchase from './containers/PagePurchase'
import PlanCard from './PlanCard'
import ProAlert from './messages/ProAlert'
import ProTag from './messages/ProTag'
import ProgressBar from './usage/ProgressBar'
import QuestionAndAnswer from './content/QuestionAndAnswer'
import QuestionMarkBlock from './funsies/QuestionMarkBlock'
import SampleSpinner from './loaders/SampleSpinner'
import SelectCustom from './forms/SelectCustom'
import Setup from './messages/Setup'
import StyleCardHorizontal from './StyleCard/StyleCardHorizontal.vue'
import StyleCardVertical from './StyleCard/StyleCardVertical.vue'
import StyleCardBrands from './StyleCard/StyleCardBrands'
import SubscriptionFeatures from './marketing/SubscriptionFeatures'
import Tag from './content/Tag'
import TextField from './forms/TextField'
import UsageMessages from './usage/UsageMessages.vue'
import YoutubeEmbed from './content/YoutubeEmbed'

/**
 * A function that registers the global components if passing both a name string and a component
 * definition. See VueJS documentation here: {@link https://v2.vuejs.org/v2/guide/components-registration.html Registration} and
 * here: {@link https://v2.vuejs.org/v2/guide/instance.html Vue Instance}
 */
export default (vueInstance) => {
  /**
   * A function that accepts a name string, a component definition, and parameter object. The parameter object contains a
   * boolean value: withNameToo, which defaults to false. If it is set to true it will ALSO register the component with the
   * component's name property.
   *
   * @param {string} name - The name associated with the app component being registered
   * @param {Object} component - The app component
   * @param {Object} params - An optional object with the "withNameToo" flag set
   */
  const comp = (name, component, params = {}) => {
    const { withNameToo = false } = params

    vueInstance.component(name, component)

    if (withNameToo && component.name) {
      vueInstance.component(component.name, component)
    }
  }

  comp('font-awesome-icon', FontAwesomeIcon)
  comp('font-awesome-layers', FontAwesomeLayers)
  comp('font-awesome-layers-text', FontAwesomeLayersText)
  comp('owa-accept-policies-checkbox', AcceptPoliciesCheckbox)
  comp('owa-action-button', ActionButton)
  comp('owa-admin-bar', AdminBar)
  comp('owa-alert-message', AlertMessage, { withNameToo: true })
  comp('owa-alert', Alert)
  comp('owa-carbon-ad', CarbonAd)
  comp('owa-code-block-tab-set', CodeBlockTabSet, { withNameToo: true })
  comp('owa-code-block', CodeBlock, { withNameToo: true })
  comp('owa-code-highlight', CodeHighlight)
  comp('owa-cookie-conset', CookieConsent)
  comp('owa-element-loading', ElementLoading)
  comp('owa-flash-message', FlashMessage)
  comp('owa-frameworks', Frameworks)
  comp('owa-logo-font-awesome', LogoFontAwesome)
  comp('owa-fontawesome-ad', FontAwesomeAd)
  comp('owa-force-update-on-mounted', ForceUpdateOnMounted)
  comp('owa-icons-loading', IconsLoading)
  comp('owa-logo-flag', LogoFlag)
  comp('owa-media', Media)
  comp('owa-message', Message)
  comp('owa-modal-auto-renew', ModalAutoRenew)
  comp('owa-modal-cancel-subscription', ModalCancelSubscription)
  comp('owa-modal-frame', ModalFrame)
  comp('owa-modal-kits-and-domains', ModalKitsAndDomains)
  comp('owa-modal-upsell', ModalUpsell)
  comp('owa-modal-use-kit', ModalUseKit)
  comp('owa-monetization-ad', MonetizationAd)
  comp('owa-nudge', Nudge)
  comp('owa-obfuscated-text', ObfuscatedText)
  comp('owa-version-alert', VersionAlert)
  comp('owa-page-focused', PageFocused)
  comp('owa-page-footer-simple', PageFooterSimple)
  comp('owa-page-footer', PageFooter)
  comp('owa-page-header-limited', PageHeaderLimited)
  comp('owa-page-header-simple', PageHeaderSimple)
  comp('owa-page-header', PageHeader)
  comp('owa-page-loading', PageLoading)
  comp('owa-page-purchase', PagePurchase)
  comp('owa-page', Page)
  comp('owa-plan-card', PlanCard)
  comp('owa-pro-alert', ProAlert, { withNameToo: true })
  comp('owa-pro-tag', ProTag)
  comp('owa-progress-bar', ProgressBar)
  comp('owa-question-and-answer', QuestionAndAnswer)
  comp('owa-question-mark-block', QuestionMarkBlock)
  comp('owa-sample-spinner', SampleSpinner)
  comp('owa-select-custom', SelectCustom)
  comp('owa-setup', Setup)
  comp('owa-style-card-horizontal', StyleCardHorizontal)
  comp('owa-style-card-vertical', StyleCardVertical)
  comp('owa-style-card-brands', StyleCardBrands)
  comp('owa-subscription-features', SubscriptionFeatures)
  comp('owa-tag', Tag)
  comp('owa-text-field', TextField)
  comp('owa-usage-messages', UsageMessages)
  comp('owa-youtube-embed', YoutubeEmbed)
}
